import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput, { formatINR } from '../../components/SliderInput';

const RetirementCalculator = () => {
  const [currentAge, setCurrentAge] = useState(30);
  const [retireAge, setRetireAge] = useState(60);
  const [monthlyExp, setMonthlyExp] = useState(50000);
  const [inflation, setInflation] = useState(6);
  const [returnRate, setReturnRate] = useState(12);
  const [postRetReturn, setPostRetReturn] = useState(7);
  const [lifeExp, setLifeExp] = useState(80);

  const calc = useMemo(() => {
    const yearsToRetire = Math.max(retireAge - currentAge, 0);
    const yearsInRetire = Math.max(lifeExp - retireAge, 1);
    // Future monthly expense (inflation adjusted)
    const futureMonthlyExp = monthlyExp * Math.pow(1 + inflation / 100, yearsToRetire);
    // Required corpus at retirement: PV of an inflation-adjusted annuity
    // Using real rate of return
    const realRate = ((1 + postRetReturn / 100) / (1 + inflation / 100) - 1);
    const months = yearsInRetire * 12;
    const monthlyRealRate = realRate / 12;
    const corpus = monthlyRealRate === 0
      ? futureMonthlyExp * months
      : futureMonthlyExp * (1 - Math.pow(1 + monthlyRealRate, -months)) / monthlyRealRate;
    // Monthly SIP needed to reach corpus
    const r = returnRate / 100 / 12;
    const n = yearsToRetire * 12;
    const sip = n === 0 || r === 0 ? corpus / Math.max(n, 1) : (corpus * r) / ((Math.pow(1 + r, n) - 1) * (1 + r));
    return { yearsToRetire, yearsInRetire, futureMonthlyExp, corpus, sip };
  }, [currentAge, retireAge, monthlyExp, inflation, returnRate, postRetReturn, lifeExp]);

  return (
    <CalculatorLayout
      title="Retirement Calculator"
      subtitle="Plan your retirement corpus considering inflation, current expenses, and expected returns."
      category="Financial"
      description={<>
        <p>This calculator helps you estimate the corpus you'll need at retirement and the monthly SIP required to build that corpus, accounting for inflation and expected market returns.</p>
        <p>It assumes your monthly expenses grow with inflation until retirement, then your corpus is drawn down over your retirement years at a (typically lower) post-retirement return rate.</p>
      </>}
      formulaNote="Real Rate = (1 + r) / (1 + i) − 1  •  Required SIP solves FV of annuity-due = Required Corpus"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <SliderInput label="Current Age" unit="yr" value={currentAge} onChange={setCurrentAge} min={18} max={70} step={1} />
          <SliderInput label="Retirement Age" unit="yr" value={retireAge} onChange={setRetireAge} min={Math.max(currentAge + 1, 35)} max={75} step={1} />
          <SliderInput label="Life Expectancy" unit="yr" value={lifeExp} onChange={setLifeExp} min={Math.max(retireAge + 1, 65)} max={100} step={1} />
          <SliderInput label="Current Monthly Expense" unit="₹" unitColor="saffron" value={monthlyExp} onChange={setMonthlyExp} min={5000} max={500000} step={1000} format={formatINR} />
          <SliderInput label="Inflation Rate" unit="%" value={inflation} onChange={setInflation} min={2} max={12} step={0.5} />
          <SliderInput label="Pre-Retirement Return" unit="%" unitColor="green" value={returnRate} onChange={setReturnRate} min={4} max={20} step={0.5} />
          <SliderInput label="Post-Retirement Return" unit="%" value={postRetReturn} onChange={setPostRetReturn} min={3} max={12} step={0.5} />
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5 h-fit md:sticky md:top-24">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Retirement Corpus Needed</div>
          <div className="font-display text-3xl md:text-4xl font-bold text-bharat-navy display-font mt-1">{formatINR(calc.corpus)}</div>

          <div className="mt-5 space-y-3">
            <Stat label="Monthly SIP Required" value={formatINR(calc.sip)} tone="saffron" big />
            <Stat label="Monthly Expense at Retirement" value={formatINR(calc.futureMonthlyExp)} />
            <Stat label="Years to Retire" value={`${calc.yearsToRetire} yr`} />
            <Stat label="Retirement Duration" value={`${calc.yearsInRetire} yr`} />
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Stat = ({ label, value, tone, big }) => {
  const c = tone === 'saffron' ? 'text-saffron-700' : tone === 'green' ? 'text-indgreen-700' : 'text-bharat-navy';
  return (
    <div className="bg-white rounded-md p-3 border border-slate-100 flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className={`display-font font-bold ${big ? 'text-xl' : 'text-sm'} ${c}`}>{value}</span>
    </div>
  );
};

export default RetirementCalculator;
